/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet-async';

import ImgixComponent from 'js/components/Imgix';
import { processImage } from 'js/lib/imgix';

import { AlwaysLightMode, Grid, Typography, breakpoints, useMediaQuery } from '@coursera/cds-core';

import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

import _t from 'i18n!nls/degree-description';

export type HeroBannerProps = {
  backgroundImageUrl: string;
  partnerLogoUrl: string;
  partnerName: string;
  programName: string;
  /**
   *  Props for DDP Revamp 2.3 Hero module change
   *  sidebarContent: renderProps for content on the right side of hero image
   */
  sidebarContent?: React.ReactElement;
  isOverviewPage?: boolean;
};

const styles = {
  containerLayout: css`
    height: 100%;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  `,
  gridContainerLayout: css`
    height: 100%;
    flex-grow: 1;
  `,
  partnerLogo: css`
    max-height: 50px;
    max-width: 100%;
  `,
  banner: css`
    position: relative;
    width: 100%;

    ${breakpoints.up('md')} {
      min-height: 500px;
    }
    ${breakpoints.down('sm')} {
      min-height: 300px;
      width: 100%;
    }

    display: flex;
    flex-direction: column;
  `,
  bannerMain: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  `,
  sidebar: css`
    display: flex;
    flex-direction: column;
    justify-content: center;

    /* margin avoids KIM section to overlap with the bottom of eoi buttons block */
    margin: 100px 0;
    ${breakpoints.down('sm')} {
      margin: 0;
    }
  `,
  /* Mobile styles */
  mobileHeight: css`
    ${breakpoints.down('sm')} {
      min-height: 500px;
    }
  `,
  partnerName: css`
    min-height: 28px;
  `,
  backgroundImage: css`
    position: absolute;
    height: 100%;
    width: 100%;
    object-fit: cover;
  `,
  backgroundImageOverlay: css`
    position: absolute;
    width: 100%;
    height: 100%;
    ${breakpoints.up('md')} {
      background: linear-gradient(90deg, rgb(0 0 0 / 100%) 0%, rgb(0 0 0 / 0%) 100%);
    }
    ${breakpoints.down('sm')} {
      box-shadow: inset 0 0 0 5000px var(--cds-color-darken-500);
    }
  `,
  ratingDesclaimer: css`
    margin-top: var(--cds-spacing-50);
  `,
};

export const HeroBanner = (props: HeroBannerProps): React.ReactElement => {
  const { backgroundImageUrl, partnerLogoUrl, partnerName, programName, sidebarContent, isOverviewPage } = props;

  const theme = useDegreeDescriptionPageTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const processedBackgroundImageUrl = processImage(backgroundImageUrl, {
    width: 1024,
    height: 768,
    quality: 30,
  });

  const optimizedBackgroundUrl = processImage(backgroundImageUrl, {
    width: 400,
    height: 300,
    quality: 7,
    blur: 50,
  });

  return (
    <div css={[styles.banner, isMobile && isOverviewPage && styles.mobileHeight]}>
      <Helmet>
        {/* preload possible background images */}
        <link rel="preload" href={optimizedBackgroundUrl} as="image" />
      </Helmet>

      {/* Equivalent to <img src="" srcSet="" sizes="" />, no significant performance differences between them
          For simplicity and readability, use picture tag here */}
      <picture>
        <source media={`(min-width:${breakpoints.values.md}px)`} srcSet={processedBackgroundImageUrl} />
        <img src={optimizedBackgroundUrl} alt="" css={styles.backgroundImage} />
      </picture>

      {/* overlay act as the gradient layer */}
      <div css={styles.backgroundImageOverlay} />
      <Container css={styles.containerLayout}>
        <Grid container css={styles.gridContainerLayout}>
          <Grid container item xs={12} md={8} css={[styles.bannerMain]} spacing={16}>
            <Grid item>
              <ImgixComponent
                src={partnerLogoUrl}
                maxHeight={50}
                imgParams={{ quality: 10 }}
                alt={_t('#{partnerName} logo', { partnerName })}
                css={styles.partnerLogo}
              />
              <AlwaysLightMode>
                {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
                <Typography
                  variant="d2semibold"
                  color="invertBody"
                  component="h1"
                  css={theme.mobileFontSizeStyle?.title}
                >
                  {programName}
                </Typography>

                {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
                <Typography variant="h2" color="invertBody" css={styles.partnerName}>
                  {partnerName}
                </Typography>
              </AlwaysLightMode>
            </Grid>
          </Grid>

          {sidebarContent && (isDesktop || (isMobile && isOverviewPage)) && (
            <Grid item xs={12} md={4} css={styles.sidebar}>
              {sidebarContent}
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(HeroBanner);
